import { z } from 'zod';

// ── Rule ──────────────────────────────────────────────────────────────────────
export const ruleSchema = z.object({
  key: z.string(),
  label: z.string(),
  required: z.boolean().optional(),
  // SCRUM-843: the threshold a NUMERIC rule is evaluated against, carried as data
  // so it can never drift from the `label` that advertises it. Previously the
  // evaluator hardcoded one value per rule KEY, which silently mis-judged every
  // product that shared a key with a different label (ICS advertised $250,000 but
  // was gated at $25,000). Optional because boolean holding-presence rules
  // ("No IRA") have no threshold; a numeric rule missing one is fail-closed by
  // `evaluateRule` and caught by the catalogue test.
  // SCRUM-890: `.nullish()` + normalise, NOT `.optional()`. The API omits these keys
  // when null, but a single explicit null used to fail the WHOLE array's safeParse and
  // silently leave the app with zero products — every customer showed "no products",
  // with a 200 response and no console error. Accepting null and folding it to
  // undefined means a serialization change can never again blank the catalogue, and
  // `undefined` keeps one single meaning downstream: the product declared none.
  threshold: z
    .number()
    .nullish()
    .transform((v) => v ?? undefined)
    .optional(),
  // SCRUM-843 follow-up: the customer type a CATEGORICAL rule demands. Same defect
  // as the numeric thresholds — `customer_type` is shared by personal AND business
  // products with labels "Personal customer" / "Business customer", but was
  // hardcoded to Personal, so every Business product evaluated as met for a Personal
  // customer. Carried as data so the label cannot disagree with what is enforced.
  expects: z
    .string()
    .nullish()
    .transform((v) => v ?? undefined)
    .optional(),
});

export type Rule = z.infer<typeof ruleSchema>;

// ── Product ───────────────────────────────────────────────────────────────────
export const productSchema = z.object({
  id: z.string(),
  sku: z.string(),
  name: z.string(),
  category: z.string(),
  summary: z.string(),
  requiredMatches: z.number().int().positive(),
  rules: z.array(ruleSchema),
  // Campaigns UI detail fields (SCRUM-204). Omitted from the API response when null
  // (the API uses JsonIgnore WhenWritingNull), so each is .optional() — a missing key
  // is accepted; an explicit null would be rejected.
  coreType: z.string().optional(),
  description: z.string().optional(),
  valueProposition: z.string().optional(),
  targetCustomer: z.string().optional(),
});

export type Product = z.infer<typeof productSchema>;

// ── API response wrapper ──────────────────────────────────────────────────────
// The bankiq-products.json file wraps products under a "products" key.
// The live GET /api/products endpoint returns a flat array.
export const productsFileSchema = z.object({
  products: z.array(productSchema),
});

export const productsResponseSchema = z.array(productSchema);
